/**
 * Reading and writing a `StrikeCriteria` — ROADMAP 5.23.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ITM AND OTM ARE A VIEW OF `ATM_OFFSET`, NOT VARIANTS OF IT
 *
 * A builder offers ATM / ITM n / OTM n, because that is how a trader says it.
 * None of them is a new way to pick a strike: all three land on a strike some
 * number of steps from the money, which `ATM_OFFSET` already stores. Adding
 * `{ kind: 'ITM', steps }` would be a second encoding of one idea — two rows
 * in every switch, two things for a resolver to agree about, and a migration
 * the day they disagree.
 *
 * So the translation lives here, in one tested pure function, and nothing
 * below the form ever hears the words ITM or OTM.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SIGN IS THE WHOLE REASON THIS IS A FUNCTION AND NOT A CAST
 *
 * `ATM_OFFSET.steps` is a DIRECTION ON THE STRIKE LADDER: positive is above
 * spot for calls and puts alike. Moneyness is not a direction, it is a
 * relationship to spot, and it points opposite ways for the two option types.
 *
 *                       below spot          ATM          above spot
 *                     ◀── steps < 0 ────────  0  ──────── steps > 0 ──▶
 *        CALL               ITM                             OTM
 *        PUT                OTM                             ITM
 *
 * "OTM 2" is therefore +2 on a call and −2 on a put, and getting that
 * backwards builds a position that is the right shape and the wrong side of
 * the money — priced plausibly, so nothing looks broken until expiry.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/** How a trader names a strike's relationship to spot. */
export const MONEYNESS = ['ITM', 'ATM', 'OTM'];
/** `ATM_OFFSET` steps for "OTM 2" on this option type. ATM ignores `n`. */
export function stepsForMoneyness(moneyness, n, optionType) {
    if (moneyness === 'ATM')
        return 0;
    const away = Math.abs(n);
    const outward = optionType === 'CE' ? away : -away;
    return moneyness === 'OTM' ? outward : -outward;
}
/** The inverse, so a stored offset can be shown the way it was entered. */
export function moneynessOf(steps, optionType) {
    if (steps === 0)
        return { moneyness: 'ATM', n: 0 };
    const above = steps > 0;
    const out = optionType === 'CE' ? above : !above;
    return { moneyness: out ? 'OTM' : 'ITM', n: Math.abs(steps) };
}
/**
 * Bounds every writer agrees on.
 *
 * Here rather than in the server's schema because the form has to refuse the
 * same values the API would, and a browser that lets a trader type 500 and
 * then shows them a server error has wasted the only moment they were
 * looking at the field.
 */
export const CRITERIA_LIMITS = {
    /**
     * How far from the money a leg may be placed, in strike steps.
     *
     * TWENTY, NOT FIFTY. Fifty steps is 2,500 points on NIFTY — far past
     * anything the exchange lists, so the bound caught nothing a trader would
     * ever type and the first real complaint came from the resolver at order
     * time ("22,050 CE is not listed for this expiry"). Twenty is 1,000
     * points, which is still wider than any quoted wing and is a number a
     * person can be told before they finish typing.
     *
     * It is also exactly where the thirty-leg speed test sits: its legs run
     * −10 to +20, so the strategy that most needs to keep working is at the
     * boundary rather than outside it.
     */
    maxSteps: 20,
    maxPremium: 100_000,
    /** A delta magnitude. Never 0 (every far wing ties) and never above 1. */
    minDelta: 0.01,
    maxDelta: 1,
    /** Most a leg may be scaled relative to the others. Matches the API. */
    maxRatio: 100,
};
/**
 * Is this a well-formed criterion?
 *
 * Returns the complaint, or null when it is fine. A string rather than a
 * boolean because every caller needs to say what is wrong: the form puts it
 * under the field and the API puts it in the response.
 */
export function strikeCriteriaProblem(c) {
    switch (c.kind) {
        case 'ATM_OFFSET':
            if (!Number.isInteger(c.steps))
                return 'Strike offset must be a whole number of steps.';
            if (Math.abs(c.steps) > CRITERIA_LIMITS.maxSteps) {
                return `Strike must be within ${CRITERIA_LIMITS.maxSteps} strikes of the money.`;
            }
            return null;
        case 'CLOSEST_PREMIUM':
            if (!(c.premium > 0))
                return 'Premium must be more than zero.';
            if (c.premium > CRITERIA_LIMITS.maxPremium)
                return 'Premium is too large.';
            return null;
        case 'PREMIUM_RANGE':
            if (!(c.min > 0))
                return 'The lowest premium must be more than zero.';
            if (!(c.max > c.min))
                return 'The highest premium must be above the lowest.';
            if (c.max > CRITERIA_LIMITS.maxPremium)
                return 'Premium is too large.';
            return null;
        case 'CLOSEST_DELTA':
            return deltaProblem(c.delta, 'Delta');
        case 'DELTA_RANGE': {
            const low = deltaProblem(c.min, 'The lowest delta');
            if (low)
                return low;
            const high = deltaProblem(c.max, 'The highest delta');
            if (high)
                return high;
            if (!(c.max > c.min))
                return 'The highest delta must be above the lowest.';
            return null;
        }
    }
}
function deltaProblem(d, subject) {
    if (!(d >= CRITERIA_LIMITS.minDelta) || !(d <= CRITERIA_LIMITS.maxDelta)) {
        return `${subject} must be between ${CRITERIA_LIMITS.minDelta} and ${CRITERIA_LIMITS.maxDelta}.`;
    }
    return null;
}
/**
 * The criterion in the shortest form that is still unambiguous.
 *
 * Shared so a leg reads identically on a card, in the builder and in a
 * deployment record. Two spellings of the same rule is how a trader comes to
 * believe they are two rules.
 */
export function strikeCriteriaLabel(c, optionType) {
    switch (c.kind) {
        case 'ATM_OFFSET': {
            const { moneyness, n } = moneynessOf(c.steps, optionType);
            return moneyness === 'ATM' ? 'ATM' : `${moneyness} ${n}`;
        }
        case 'CLOSEST_PREMIUM':
            return `₹${c.premium}`;
        case 'PREMIUM_RANGE':
            return `₹${c.min}–${c.max}`;
        case 'CLOSEST_DELTA':
            return `${c.delta}Δ`;
        case 'DELTA_RANGE':
            return `${c.min}–${c.max}Δ`;
    }
}
/** Does picking this strike need a solved delta? */
export function needsGreeks(kind) {
    return kind === 'CLOSEST_DELTA' || kind === 'DELTA_RANGE';
}
//# sourceMappingURL=strikeCriteria.js.map